import type { Recipe } from '../types';

// Mississippi pot roast with no packets: a homemade ranch blend, organic beef
// base and Worcestershire in place of au jus mix, organic carrots and butter.
// Same flavor as the classic, rounder and beefier, nothing on the label you
// cannot pronounce. Seared first because without the packets the browning is
// where the depth comes from. The packet version is mississippi-pot-roast.

export const homemadeMississippiPotRoast: Recipe = {
  slug: 'homemade-mississippi-pot-roast',
  title: 'Homemade Mississippi Pot Roast',
  meal: 'dinner',
  category: 'meal',
  types: ['stew', 'sandwich'],
  cuisines: ['american'],
  traits: ['freezer-friendly', 'one-pot', 'hidden-veg', 'high-protein', 'make-ahead'],
  yield: '8 servings',
  activeTime: '25 min',
  totalTime: '1 hr 50 min',
  freezerLife: '3 months',
  difficulty: 1,

  summary:
    'The Mississippi pot roast everybody knows, with no packets. A homemade ranch blend and organic beef base stand in for the mixes, and organic carrots and onion get mashed into the gravy. Same buttery, tangy roast with a clean label.',

  flavorNotes:
    'Rich, beefy and buttery with the same tangy pepperoncini edge as the classic. Without the packets it comes out less salty and more beefy, and the dill and chives taste like herbs instead of powder. Not spicy. If it tastes flat at the end, add a splash more brine first and salt second.',

  ingredients: [
    {
      id: 'beef',
      name: 'Chuck roast (or arm / shoulder roast)',
      total: '3 lb',
      prep: 'thawed, cut into 3 chunks, patted dry; see the step 1 tip for which cuts work',
      fromFreezer: true,
      uses: [{ step: 1, amount: '3 lb' }],
    },
    {
      id: 'avocado-oil',
      name: 'Chosen Foods avocado oil',
      total: '1 tbsp',
      uses: [{ step: 1, amount: '1 tbsp' }],
    },
    {
      id: 'pepper',
      name: 'Black pepper',
      total: '1 tsp',
      uses: [{ step: 1, amount: '1 tsp' }],
    },
    {
      id: 'water',
      name: 'Water',
      total: '1 cup',
      shop: false,
      uses: [{ step: 2, amount: '1 cup' }],
    },
    {
      id: 'onion',
      name: 'Yellow onion',
      total: '1 large',
      prep: 'cut into thick wedges',
      uses: [{ step: 2, amount: '1 large' }],
      shop: 'Fresh yellow onion, each (loose), 1 large',
    },
    {
      id: 'carrots',
      name: 'Organic carrots',
      total: '1 lb',
      prep: 'peeled, cut into 2-inch chunks',
      uses: [{ step: 2, amount: '1 lb' }],
      shop: 'Marketside Organic carrots, 1 lb bag',
    },
    {
      id: 'beef-base',
      name: 'Better Than Bouillon Organic Roasted Beef base',
      total: '1 tbsp',
      uses: [{ step: 3, amount: '1 tbsp' }],
      shop: 'Better Than Bouillon Organic Roasted Beef base, 8 oz jar',
    },
    {
      id: 'worcestershire',
      name: 'Lea & Perrins Worcestershire',
      total: '1 tbsp',
      uses: [{ step: 3, amount: '1 tbsp' }],
    },
    { id: 'parsley-dried', name: 'Dried parsley', total: '1 tbsp', uses: [{ step: 3, amount: '1 tbsp', note: 'ranch blend' }] },
    { id: 'dill', name: 'Dried dill weed', total: '1 tsp', uses: [{ step: 3, amount: '1 tsp', note: 'ranch blend' }] },
    { id: 'chives', name: 'Dried chives', total: '1 tsp', uses: [{ step: 3, amount: '1 tsp', note: 'ranch blend' }] },
    { id: 'garlic-powder', name: 'Garlic powder', total: '1 tsp', uses: [{ step: 3, amount: '1 tsp', note: 'ranch blend' }] },
    { id: 'onion-powder', name: 'Onion powder', total: '2 tsp', uses: [{ step: 3, amount: '2 tsp', note: 'ranch blend' }] },
    { id: 'salt', name: 'Salt', total: '1 tsp', uses: [{ step: 3, amount: '1 tsp' }] },
    {
      id: 'pepperoncini',
      name: 'Whole pepperoncini peppers, plus brine',
      total: '8 peppers + ¼ cup brine',
      prep: 'every brand at Walmart has dye and sulfites; GV is as clean as it gets there',
      uses: [{ step: 3, amount: '8 peppers + ¼ cup brine' }],
      shop: 'Great Value whole pepperoncini, 12 fl oz jar',
    },
    {
      id: 'butter',
      name: 'Organic unsalted butter',
      total: '4 tbsp',
      prep: 'half a stick, in pats',
      uses: [{ step: 3, amount: '4 tbsp' }],
      shop: 'Organic Valley unsalted butter, 8 oz (2 sticks)',
    },
    {
      id: 'cornstarch',
      name: 'Cornstarch',
      total: '2 tbsp',
      optional: true,
      optionalKind: 'upgrade',
      optionalWhy: 'Turns the juice into gravy for potatoes. Skip it for sandwiches, because thin juice soaks the roll better.',
      uses: [{ step: 6, amount: '2 tbsp' }],
    },
    {
      id: 'potatoes',
      name: 'Russet potatoes, for mashing',
      total: '3 lb',
      optional: true,
      optionalKind: 'serve-with',
      optionalWhy: 'The classic plate. Hoagie rolls from this cookbook are the other move.',
      uses: [{ step: 7, amount: '3 lb' }],
      shop: 'Russet potatoes, 5 lb bag',
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Sear the roast',
      time: '12 min',
      ingredientIds: ['beef', 'avocado-oil', 'pepper'],
      body:
        'Cut the {3 lb|beef} roast into 3 roughly equal chunks and pat them very dry. Rub with the {1 tsp|pepper} of black pepper. Set the Instant Pot to Sauté on High, add the {1 tablespoon|avocado-oil} of avocado oil, and when it shimmers, sear the chunks about 4 minutes a side until deeply browned. Do it in two rounds if they crowd. Move them to a plate.',
      tip:
        'Cuts that work: chuck roast, chuck eye, arm roast, shoulder / clod roast, blade or 7-bone roast, cross-rib (English) roast, brisket point. Anything marbled with seams works. Pikes Peak (heel of round) works but runs leaner and stringier, so add 15 minutes at step 4. Bottom round, rump and sirloin tip are passable the same way. Do not use eye of round, top round, sirloin, tri-tip, rib roast or tenderloin. They dry out and will not shred.',
    },
    {
      n: 2,
      title: 'Deglaze and build the veg bed',
      time: '3 min',
      critical: true,
      ingredientIds: ['water', 'onion', 'carrots'],
      body:
        'Hit Cancel. Pour in the {1 cup|water} of water and scrape every browned bit off the bottom with a wooden spoon until the pot feels smooth. That scrape is what prevents the Burn warning. Spread the onion wedges and the {1 lb|carrots} of carrot chunks across the bottom, then set the seared beef on top with any juices from the plate.',
    },
    {
      n: 3,
      title: 'Season',
      time: '3 min',
      ingredientIds: ['beef-base', 'worcestershire', 'parsley-dried', 'dill', 'chives', 'garlic-powder', 'onion-powder', 'salt', 'pepperoncini', 'butter'],
      body:
        'Whisk the {1 tablespoon|beef-base} of beef base and the {1 tablespoon|worcestershire} of Worcestershire into the liquid around the beef. Mix the {1 tablespoon|parsley-dried} of dried parsley, {1 tsp|dill} of dill, {1 tsp|chives} of chives, {1 tsp|garlic-powder} of garlic powder, {2 tsp|onion-powder} of onion powder and {1 tsp|salt} of salt, and sprinkle that ranch blend over the top of the beef. Tuck the pepperoncinis around the meat, pour {¼ cup} of their brine over the top, and lay the {4 tablespoons|butter} of butter in pats across the roast. Do not stir.',
      tip:
        'The dry blend goes on the meat, not the pot floor. Powder sitting on the bottom of an Instant Pot scorches.',
    },
    {
      n: 4,
      title: 'Pressure cook',
      time: '60 min + 15 min release',
      ingredientIds: [],
      body:
        'Lock the lid, valve to Sealing, Pressure Cook on High for 60 minutes. When it beeps, do a natural release: let the pressure drop on its own for 15 minutes, then turn the valve to Venting for the rest.',
      tip:
        'Venting straight off high pressure makes the meat tighten up. For a leaner cut like Pikes Peak, make it 75 minutes.',
    },
    {
      n: 5,
      title: 'Shred and hide the vegetables',
      time: '8 min',
      critical: true,
      ingredientIds: [],
      body:
        'Lift the beef onto a board and shred it with two forks, pulling out the big fat pieces. Mash the carrots and onion right in the pot with a potato masher, or use an immersion blender, until they disappear into the juice. Slice the pepperoncinis if anyone is picky about biting into a whole one.',
      tip:
        'A pound of carrots blended into the juice reads as a slightly sweeter, thicker gravy. Nobody finds a carrot.',
    },
    {
      n: 6,
      title: 'Thicken (optional) and return the meat',
      time: '5 min',
      ingredientIds: ['cornstarch'],
      body:
        'For gravy over potatoes: set the pot to Sauté, stir the {2 tablespoons|cornstarch} of cornstarch into {3 tablespoons} of cold water, and whisk it into the bubbling juice for 1 to 2 minutes. For sandwiches, skip it. Turn the pot off, stir the shredded beef back in, and taste. Add more brine first if it is flat, then salt.',
    },
    {
      n: 7,
      title: 'Serve',
      time: '—',
      ingredientIds: ['potatoes'],
      body:
        'Over mashed potatoes with a ladle of gravy, piled on hoagie rolls with provolone under the broiler, or over rice.',
    },
  ],

  freezing:
    'Cool completely, then pack quart bags with plenty of juice in each. The juice keeps shredded beef from drying out on reheat. Freeze flat with the air pressed out, about 3 months. Gravy thickened with cornstarch comes back a little thinner and thickens again with a minute of simmering.',

  reheat: [
    {
      method: 'Stovetop',
      best: true,
      detail: 'Thaw overnight. Covered saucepan on medium-low with a splash of water, about 10 minutes. Keep it at a simmer, not a boil, or the beef goes stringy.',
    },
    {
      method: 'Microwave',
      detail: 'Thawed, covered, 70% power in 90-second rounds, stirring between. Make sure there is juice in the bowl.',
    },
    {
      method: 'Instant Pot from frozen',
      detail: 'Frozen block in with ½ cup water, Pressure Cook 10 minutes, quick release.',
    },
  ],

  glossary: [
    { term: 'Sauté', plain: 'The Instant Pot setting that turns it into a stovetop pan. The lid stays off and it heats from the bottom. Use it on High for searing.' },
    { term: 'natural release', plain: 'Letting the pot lose pressure on its own instead of flipping the valve. The meat relaxes as the pressure drops slowly, which keeps it tender.' },
    { term: 'Burn', plain: 'The Instant Pot warning that something is scorching on the bottom. It stops cooking. Usually caused by stuck bits or dry seasoning on the pot floor.' },
    { term: 'shred', plain: 'Pull cooked meat apart along its grain with two forks. If it fights you, it needs more time under pressure, not more force.' },
  ],

  inspiration: [
    { label: 'McCormick — Instant Pot Mississippi Roast', url: 'https://mccormick.com/recipes/main-dishes/instant-pot-mississippi-roast' },
    { label: 'Pip and Ebby — Instant Pot Mississippi Pot Roast', url: 'https://pipandebby.com/wprm_print/instant-pot-mississippi-pot-roast-recipe' },
  ],

  whereWeVeered:
    'The classic leans on two seasoning packets. Here they are replaced with a homemade ranch blend (dried parsley, dill, chives, garlic and onion powder) and organic Better Than Bouillon plus Worcestershire in place of the au jus mix. Without the packets the sear does the heavy lifting on flavor, so it is not optional here. Organic carrots, butter and beef base where Walmart has them; onions are conventional because Walmart has no single organic onion, and onions carry very little pesticide anyway. Butter is cut to half a stick. For the quick packet version, see Mississippi Pot Roast.',
};
